/**
 * Onboarding Completion Utilities
 * 
 * Centralized logic for determining if a client/flow is fully onboarded.
 * 
 * Business Rules:
 * - Prepayment clients (credit_status = 'not_requested'): Need 3 roles (AM, FM, CM)
 * - Credit clients (any other credit_status): Need 4 roles (AM, FM, CM, AO)
 */

export interface OnboardingRoleStatus {
  account_manager_completed: boolean;
  finance_manager_completed: boolean;
  creative_manager_completed: boolean;
  account_owner_completed: boolean;
}

export interface OnboardingCompletionContext {
  credit_status?: string | null;
  payment_method?: string | null;
  has_credit_application?: boolean;
  credit_application_status?: string | null;
}

/**
 * Determines if the account owner role is required for this flow/client
 * 
 * Account Owner is ONLY required when:
 * - credit_status is NOT 'not_requested' (meaning credit has been requested)
 * - OR payment_method is 'Credit' AND has_credit_application is true
 * 
 * For prepayment clients (credit_status = 'not_requested'), AO is optional.
 */
export function isAccountOwnerRequired(context: OnboardingCompletionContext): boolean {
  const { credit_status, payment_method, has_credit_application, credit_application_status } = context;
  
  // If credit has not been requested, AO is not required (prepayment terms)
  if (credit_status === 'not_requested' || credit_status === null || credit_status === undefined) {
    // Exception: if they explicitly have a credit application in progress
    if (has_credit_application && credit_application_status && credit_application_status !== 'not_started') {
      return true;
    }
    return false;
  }
  
  // Any other credit status means credit was requested, so AO is required
  return true;
}

/**
 * Determines if onboarding is complete based on role completion and credit status
 */
export function isOnboardingComplete(
  roleStatus: OnboardingRoleStatus,
  context: OnboardingCompletionContext
): boolean {
  // Base roles must always be complete
  const baseRolesComplete = 
    roleStatus.account_manager_completed &&
    roleStatus.finance_manager_completed &&
    roleStatus.creative_manager_completed;
  
  if (!baseRolesComplete) {
    return false;
  }
  
  // Check if AO is required
  if (isAccountOwnerRequired(context)) {
    // For credit clients, AO must complete their onboarding form
    // Credit agreement signing triggers the AO onboarding flow,
    // but completion requires the AO to submit their form
    return roleStatus.account_owner_completed;
  }
  
  // Prepayment clients are complete with just base roles
  return true;
}

/**
 * Returns the list of required roles based on credit status
 */
export function getRequiredRoles(context: OnboardingCompletionContext): string[] {
  const baseRoles = ['account_manager', 'finance_manager', 'creative_manager'];
  
  if (isAccountOwnerRequired(context)) {
    return [...baseRoles, 'account_owner'];
  }
  
  return baseRoles;
}

/**
 * Returns missing roles based on completion status and requirements
 */
export function getMissingRoles(
  roleStatus: OnboardingRoleStatus,
  context: OnboardingCompletionContext
): string[] {
  const missing: string[] = [];
  
  if (!roleStatus.account_manager_completed) {
    missing.push('Account Manager');
  }
  if (!roleStatus.finance_manager_completed) {
    missing.push('Finance Manager');
  }
  if (!roleStatus.creative_manager_completed) {
    missing.push('Creator Manager');
  }
  
  // Only check AO if required
  if (isAccountOwnerRequired(context)) {
    if (!roleStatus.account_owner_completed) {
      missing.push('Account Owner');
    }
  }
  
  return missing;
}

/**
 * Calculates completion percentage based on required roles
 */
export function calculateCompletionPercentage(
  roleStatus: OnboardingRoleStatus,
  context: OnboardingCompletionContext
): number {
  const requiredRoles = getRequiredRoles(context);
  const totalRequired = requiredRoles.length;
  
  if (totalRequired === 0) return 100;
  
  let completed = 0;
  
  if (roleStatus.account_manager_completed && requiredRoles.includes('account_manager')) {
    completed++;
  }
  if (roleStatus.finance_manager_completed && requiredRoles.includes('finance_manager')) {
    completed++;
  }
  if (roleStatus.creative_manager_completed && requiredRoles.includes('creative_manager')) {
    completed++;
  }
  if (requiredRoles.includes('account_owner')) {
    if (roleStatus.account_owner_completed) {
      completed++;
    }
  }
  
  return Math.round((completed / totalRequired) * 100);
}
